import { usePaged, Paginator } from "@/components/Paginator";
import { useState, useEffect } from "react";
import { useFetch } from "@/hooks/useFetch";
import api, { apiError } from "@/lib/api";
import { Loading, ErrorState, Empty } from "@/components/DataState";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Pencil, Trash2, Check, X, UserCheck, KeyRound, Bus, AlertOctagon, Gift, Trophy, Plus, AlertTriangle } from "lucide-react";
import panelApi from "@/lib/panelApi";
import { usePayBases } from "@/lib/usePayBases";
import { usePhones, fmtPhone } from "@/components/ReceiptWhatsApp";
import { brl } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { isGestor } from "@/lib/permissions";
import { ManualPjDialog } from "@/components/ManualPjDialog";

const roleBadge = (role) => {
  const map = { gestor: "bg-orange-500/15 text-orange-300", funcionario: "bg-sky-500/15 text-sky-300", entregador: "bg-slate-700 text-slate-300" };
  return <span className={`rounded px-2 py-0.5 text-xs font-semibold capitalize ${map[role] || "bg-slate-700 text-slate-300"}`}>{role}</span>;
};
const isFuncionario = (c) => c.role === "funcionario";

function BenefitsDialog({ collaborator, onClose }) {
  const { data, loading, error, refetch } = useFetch(`/admin/drivers/${collaborator.id}/benefits`);
  const [meal, setMeal] = useState("");
  const [commute, setCommute] = useState("");
  const [tier, setTier] = useState("none");
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (!data) return;
    const c = data.config || {};
    setMeal(c.meal_override != null ? String(c.meal_override) : "");
    setCommute(c.commute_override != null ? String(c.commute_override) : "");
    setTier(data.bonus?.tier_id || "none");
  }, [data]);

  const cfg = data?.config || {};
  const totals = data?.totals || {};
  const tiers = data?.bonus?.tiers || [];
  const mealCustom = cfg.meal_override != null;
  const commuteCustom = cfg.commute_override != null;

  const put = async (key, path, body, msg) => {
    setBusy(key);
    try {
      await api.put(path, body);
      toast.success(msg);
      await refetch();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };
  const saveMeal = () => put("meal", `/admin/users/${collaborator.id}/meal`, { value: meal === "" ? null : Number(meal) }, "Vale alimentação atualizado");
  const saveCommute = () => put("commute", `/admin/users/${collaborator.id}/commute`, { value: commute === "" ? null : Number(commute) }, "Auxílio deslocamento atualizado");
  const saveTier = () => put("tier", `/admin/users/${collaborator.id}/bonus-tier`, { tier_id: tier === "none" ? null : tier }, "Categoria de desempenho atualizada");

  const CustomBadge = ({ custom }) => (
    <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${custom ? "bg-orange-500/20 text-orange-300" : "bg-slate-700 text-slate-400"}`}>
      {custom ? "Personalizado" : "Padrão da categoria"}
    </span>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg border-slate-800 bg-slate-900" data-testid="benefits-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-100"><Gift className="h-5 w-5 text-emerald-400" /> Benefícios individuais — {collaborator.name}</DialogTitle>
          <DialogDescription className="text-slate-400">Valores por dia. Deixe em branco para usar o padrão da categoria ({collaborator.vehicle_type || collaborator.category || "—"}).</DialogDescription>
        </DialogHeader>
        {loading ? <Loading /> : error ? <ErrorState error={error} onRetry={refetch} /> : (
          <div className="space-y-4">
            {/* Vale alimentação */}
            <div className="rounded-lg border border-slate-800 bg-slate-950/40 p-3" data-testid="benefit-meal">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-200">Vale alimentação</span>
                <CustomBadge custom={mealCustom} />
              </div>
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Label className="text-xs text-slate-400">Valor por dia (R$) · padrão {brl(cfg.meal_default)}</Label>
                  <Input type="number" step="0.01" min="0" value={meal} onChange={(e) => setMeal(e.target.value)} placeholder={`Padrão (${brl(cfg.meal_default)})`} data-testid="benefit-meal-input" className="mt-1 border-slate-700 bg-slate-950 text-slate-100" />
                </div>
                <Button className="bg-orange-500 text-white hover:bg-orange-600" disabled={busy === "meal"} onClick={saveMeal} data-testid="benefit-meal-save">Salvar</Button>
              </div>
            </div>

            {/* Auxílio deslocamento */}
            <div className="rounded-lg border border-slate-800 bg-slate-950/40 p-3" data-testid="benefit-commute">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-200">Auxílio deslocamento</span>
                <CustomBadge custom={commuteCustom} />
              </div>
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Label className="text-xs text-slate-400">Valor por dia (R$) · padrão {brl(cfg.commute_default)}</Label>
                  <Input type="number" step="0.01" min="0" value={commute} onChange={(e) => setCommute(e.target.value)} placeholder={`Padrão (${brl(cfg.commute_default)})`} data-testid="benefit-commute-input" className="mt-1 border-slate-700 bg-slate-950 text-slate-100" />
                </div>
                <Button className="bg-orange-500 text-white hover:bg-orange-600" disabled={busy === "commute"} onClick={saveCommute} data-testid="benefit-commute-save">Salvar</Button>
              </div>
            </div>

            {/* Vale combustível (automático) */}
            <div className="rounded-lg border border-slate-800 bg-slate-950/40 p-3" data-testid="benefit-fuel">
              <div className="mb-1 flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-200">Vale combustível</span>
                <span className="rounded bg-slate-700 px-1.5 py-0.5 text-[10px] font-semibold text-slate-400">Automático por rota</span>
              </div>
              <p className="text-xs text-slate-400">Preço por km: <span className="font-mono text-slate-200">{brl(cfg.fuel_price)}</span> · total no período: <span className="font-mono text-slate-200">{brl(totals.fuel)}</span></p>
            </div>

            {/* Categoria de desempenho (bônus) */}
            <div className="rounded-lg border border-slate-800 bg-slate-950/40 p-3" data-testid="benefit-tier">
              <div className="mb-2 flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-200"><Trophy className="h-4 w-4 text-amber-400" /> Categoria de desempenho</span>
                <span className="text-xs text-slate-400">bônus atual: <span className="font-mono text-amber-300">{brl(data?.bonus?.value)}</span></span>
              </div>
              {tiers.length === 0 ? (
                <p className="text-xs text-slate-500">Nenhuma categoria cadastrada. Crie categorias em Folha → Configuração de salário.</p>
              ) : (
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <Select value={tier} onValueChange={setTier}>
                      <SelectTrigger data-testid="benefit-tier-select" className="border-slate-700 bg-slate-950 text-slate-100"><SelectValue placeholder="Selecione" /></SelectTrigger>
                      <SelectContent className="border-slate-700 bg-slate-900 text-slate-100">
                        <SelectItem value="none">Sem categoria</SelectItem>
                        {tiers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} — {brl(t.value)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button className="bg-orange-500 text-white hover:bg-orange-600" disabled={busy === "tier"} onClick={saveTier} data-testid="benefit-tier-save">Salvar</Button>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Colaboradores() {
  const { data, loading, error, refetch } = useFetch("/admin/collaborators");
  const pending = useFetch("/admin/pending-funcionarios");
  const bases = useFetch("/admin/bases");
  const driversData = useFetch("/admin/drivers");
  const pjSettings = useFetch("/admin/payroll-settings");
  const [filter, setFilter] = useState("all");
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", vehicle_type: "", base_ids: [], contract_type: "clt", pj_rate: "", pay_base: "", phone: "" });
  const payBases = usePayBases();
  const phones = usePhones();
  const [cred, setCred] = useState(null);
  const [credForm, setCredForm] = useState({ email: "", password: "" });
  const [commute, setCommute] = useState(null);
  const [commuteVal, setCommuteVal] = useState("");
  const [del, setDel] = useState(null);
  const [purge, setPurge] = useState(null);
  const [purgeForm, setPurgeForm] = useState({ email: "", password: "" });
  const [benefits, setBenefits] = useState(null);
  const [busy, setBusy] = useState(null);
  const [pjOpen, setPjOpen] = useState(false);
  const { user } = useAuth();
  const all = data?.collaborators || [];
  const list = all.filter((c) => (filter === "all" ? true : c.role === filter));
  const pg = usePaged(list, 25);

  if (loading) return <Loading />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;

  const pendingList = pending.data?.pending || [];
  const baseList = bases.data?.bases || [];
  const baseIdsById = {};
  (driversData.data?.drivers || []).forEach((d) => { baseIdsById[d.id] = d.base_ids || []; });
  const pjById = {};
  ["ciclista_pj", "motociclista_pj"].forEach((s) => (pjSettings.data?.sections?.[s]?.drivers || []).forEach((d) => { pjById[d.id] = d.pj_rate_override; }));

  const openEdit = (c) => {
    const isPj = Object.prototype.hasOwnProperty.call(pjById, c.id);
    setForm({
      name: c.name || "",
      email: c.email || "",
      vehicle_type: c.vehicle_type || "",
      base_ids: baseIdsById[c.id] || c.base_ids || [],
      contract_type: isPj ? "pj" : "clt",
      pj_rate: isPj && pjById[c.id] != null ? String(pjById[c.id]) : "",
      pay_base: payBases.data?.[c.id] || "",
      phone: fmtPhone(phones.data?.[c.id]),
    });
    setEdit(c);
  };

  const saveEdit = async () => {
    setBusy("edit");
    try {
      const path = isFuncionario(edit) ? `/admin/funcionarios/${edit.id}` : `/admin/drivers/${edit.id}`;
      const body = { name: form.name, email: form.email };
      if (!isFuncionario(edit) && form.vehicle_type) body.vehicle_type = form.vehicle_type;
      if (!isFuncionario(edit)) {
        body.contract_type = form.contract_type;
        body.pj_rate_override = form.contract_type === "pj" && form.pj_rate !== "" ? Number(form.pj_rate) : null;
      }
      body.base_ids = form.base_ids;
      await api.put(path, body);
      if (isFuncionario(edit) && (payBases.data?.[edit.id] || "") !== form.pay_base) {
        await panelApi.put(`/panel/pay-bases/${edit.id}`, { base_id: form.pay_base || null });
        await payBases.refetch();
      }
      if (fmtPhone(phones.data?.[edit.id]) !== form.phone) {
        await panelApi.put(`/panel/phone/${edit.id}`, { phone: form.phone });
        await phones.refetch();
      }
      toast.success("Colaborador atualizado");
      setEdit(null);
      await Promise.allSettled([refetch(), driversData.refetch(), pjSettings.refetch()]);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };

  const saveCred = async () => {
    setBusy("cred");
    try {
      const body = {};
      if (credForm.email) body.email = credForm.email;
      if (credForm.password) body.password = credForm.password;
      await api.put(`/admin/users/${cred.id}/credentials`, body);
      toast.success("Credenciais atualizadas");
      await refetch(); setCred(null);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };

  const saveCommute = async () => {
    setBusy("commute");
    try {
      await api.put(`/admin/users/${commute.id}/commute`, { value: commuteVal === "" ? null : Number(commuteVal) });
      toast.success("Auxílio deslocamento atualizado");
      await refetch(); setCommute(null);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };

  const confirmDelete = async () => {
    setBusy("del");
    try {
      const path = isFuncionario(del) ? `/admin/funcionarios/${del.id}` : `/admin/drivers/${del.id}`;
      await api.delete(path);
      toast.success("Colaborador removido");
      await refetch(); setDel(null);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };

  const confirmPurge = async () => {
    setBusy("purge");
    try {
      const body = { email_confirmation: purgeForm.email.trim() };
      if (purgeForm.password) body.gestor_password = purgeForm.password;
      await api.post(`/admin/users/${purge.id}/purge`, body);
      toast.success("Colaborador excluído permanentemente");
      await Promise.all([refetch(), pending.refetch()]);
      setPurge(null);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };

  const decide = async (fid, action) => {
    setBusy(`${fid}-${action}`);
    try {
      await api.post(`/admin/funcionarios/${fid}/${action}`);
      toast.success(action === "approve" ? "Cadastro aprovado" : "Cadastro rejeitado");
      await Promise.all([pending.refetch(), refetch()]);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };

  const filters = [
    { k: "all", label: "Todos" }, { k: "gestor", label: "Gestores" },
    { k: "funcionario", label: "Funcionários" }, { k: "entregador", label: "Entregadores" },
  ];

  return (
    <div className="space-y-6" data-testid="colaboradores">
      {pendingList.length > 0 && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-5">
          <div className="mb-3 flex items-center gap-2">
            <UserCheck className="h-4 w-4 text-amber-400" />
            <h3 className="text-base font-medium text-amber-200">Cadastros pendentes de aprovação</h3>
          </div>
          <div className="space-y-2">
            {pendingList.map((p) => (
              <div key={p.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-slate-900 px-4 py-3">
                <div><p className="font-medium text-slate-100">{p.name}</p><p className="text-xs text-slate-500">{p.email} • {p.category || p.role}</p></div>
                <div className="flex gap-2">
                  <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700" disabled={busy === `${p.id}-approve`} onClick={() => decide(p.id, "approve")} data-testid={`approve-${p.id}`}><Check className="h-4 w-4" /> Aprovar</Button>
                  <Button size="sm" variant="outline" className="border-slate-700 text-slate-300" disabled={busy === `${p.id}-reject`} onClick={() => decide(p.id, "reject")} data-testid={`reject-${p.id}`}><X className="h-4 w-4" /> Rejeitar</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {filters.map((f) => (
          <button key={f.k} onClick={() => setFilter(f.k)} data-testid={`colab-filter-${f.k}`}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${filter === f.k ? "bg-orange-500 text-white" : "bg-slate-800 text-slate-300 hover:bg-slate-700"}`}>{f.label}</button>
        ))}
        <span className="ml-auto self-center text-sm text-slate-500">{list.length} colaborador(es)</span>
        {isGestor(user) && (
          <Button size="sm" className="bg-orange-500 text-white hover:bg-orange-600" onClick={() => setPjOpen(true)} data-testid="colab-add-pj">
            <Plus className="h-4 w-4" /> Cadastrar PJ
          </Button>
        )}
      </div>
      <ManualPjDialog open={pjOpen} onClose={() => setPjOpen(false)} bases={baseList}
        onSaved={() => Promise.allSettled([refetch(), driversData.refetch(), pjSettings.refetch()])} />

      <div className="overflow-x-auto rounded-xl border border-slate-800 bg-slate-900">
        <table className="w-full text-sm" data-testid="colab-table">
          <thead>
            <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3 font-semibold">Nome</th><th className="px-4 py-3 font-semibold">E-mail</th>
              <th className="px-4 py-3 font-semibold">Perfil</th><th className="px-4 py-3 font-semibold">Categoria</th>
              <th className="px-4 py-3 font-semibold">Status</th><th className="px-4 py-3 text-right font-semibold">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {list.length === 0 && <tr><td colSpan={6}><Empty /></td></tr>}
            {pg.items.map((c) => (
              <tr key={c.id} className="hover:bg-slate-800/40">
                <td className="px-4 py-3 font-medium text-slate-100">{c.name}</td>
                <td className="px-4 py-3 text-slate-400">{c.email}</td>
                <td className="px-4 py-3"><span className="flex flex-wrap items-center gap-1.5">{roleBadge(c.role)}{isFuncionario(c) && payBases.isSuccess && !payBases.data?.[c.id] && <span title="Defina a base de pagamento antes de gerar a folha" className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-300" data-testid={`colab-no-pay-base-${c.id}`}><AlertTriangle className="h-3 w-3" /> Sem base de pagamento</span>}</span></td>
                <td className="px-4 py-3 capitalize text-slate-400" data-testid={`colab-cat-${c.id}`}>
                  {c.vehicle_type || c.category || "—"}
                  {c.contract_type === "pj" && <span className="ml-1.5 rounded border border-sky-500/30 px-1 py-px text-[10px] font-semibold uppercase not-italic text-sky-300" data-testid={`colab-pj-badge-${c.id}`}>PJ</span>}
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs ${c.approval_status === "approved" ? "text-emerald-400" : "text-amber-400"}`}>{c.approval_status === "approved" ? "Aprovado" : c.approval_status || "—"}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" className="h-8 px-2 text-orange-300 hover:text-orange-200" onClick={() => openEdit(c)} data-testid={`colab-edit-${c.id}`} title="Editar"><Pencil className="h-4 w-4" /></Button>
                    {!c.is_manual && <Button size="sm" variant="ghost" className="h-8 px-2 text-sky-300 hover:text-sky-200" onClick={() => { setCredForm({ email: c.email || "", password: "" }); setCred(c); }} data-testid={`colab-cred-${c.id}`} title="Credenciais"><KeyRound className="h-4 w-4" /></Button>}
                    {!isFuncionario(c) && (
                      <Button size="sm" variant="ghost" className="h-8 px-2 text-emerald-300 hover:text-emerald-200" onClick={() => setBenefits(c)} data-testid={`colab-benefits-${c.id}`} title="Benefícios individuais"><Gift className="h-4 w-4" /></Button>
                    )}
                    {!isFuncionario(c) && (
                      <Button size="sm" variant="ghost" className="h-8 px-2 text-violet-300 hover:text-violet-200" onClick={() => { setCommuteVal(c.commute_override != null ? String(c.commute_override) : ""); setCommute(c); }} data-testid={`colab-commute-${c.id}`} title="Auxílio deslocamento"><Bus className="h-4 w-4" /></Button>
                    )}
                    <Button size="sm" variant="ghost" className="h-8 px-2 text-rose-400 hover:text-rose-300" onClick={() => setDel(c)} data-testid={`colab-delete-${c.id}`} title="Remover"><Trash2 className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" className="h-8 px-2 text-rose-500 hover:bg-rose-500/10 hover:text-rose-400" onClick={() => { setPurgeForm({ email: "", password: "" }); setPurge(c); }} data-testid={`colab-purge-${c.id}`} title="Excluir permanentemente"><AlertOctagon className="h-4 w-4" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Paginator pg={pg} testId="colab-pager" />
      </div>

      {/* Edit */}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-md border-slate-800 bg-slate-900">
          <DialogHeader>
            <DialogTitle className="text-slate-100">Editar colaborador</DialogTitle>
            <DialogDescription className="text-slate-400">Atualize os dados cadastrais do colaborador.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label className="text-slate-300">Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="colab-name" className="border-slate-700 bg-slate-950 text-slate-100" /></div>
            <div className="space-y-2"><Label className="text-slate-300">E-mail</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="colab-email" className="border-slate-700 bg-slate-950 text-slate-100" /></div>
            <div className="space-y-2"><Label className="text-slate-300">WhatsApp</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(85) 99999-9999" data-testid="colab-phone" className="border-slate-700 bg-slate-950 text-slate-100" /><p className="text-xs text-slate-500">Usado para enviar o detalhamento da folha e os recibos.</p></div>
            {edit && !isFuncionario(edit) && (
              <div className="space-y-2">
                <Label className="text-slate-300">Tipo de veículo</Label>
                <Select value={form.vehicle_type} onValueChange={(v) => setForm({ ...form, vehicle_type: v })}>
                  <SelectTrigger data-testid="colab-vehicle" className="border-slate-700 bg-slate-950 text-slate-100"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent className="border-slate-700 bg-slate-900 text-slate-100">
                    <SelectItem value="ciclista">Ciclista</SelectItem>
                    <SelectItem value="motociclista">Motoboy</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {edit && !isFuncionario(edit) && (
              <div className="space-y-2">
                <Label className="text-slate-300">Tipo de contrato</Label>
                <Select value={form.contract_type} onValueChange={(v) => setForm({ ...form, contract_type: v })}>
                  <SelectTrigger data-testid="colab-contract" className="border-slate-700 bg-slate-950 text-slate-100"><SelectValue /></SelectTrigger>
                  <SelectContent className="border-slate-700 bg-slate-900 text-slate-100">
                    <SelectItem value="clt">CLT</SelectItem>
                    <SelectItem value="pj">PJ</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {edit && !isFuncionario(edit) && form.contract_type === "pj" && (
              <div className="space-y-2">
                <Label className="text-slate-300">Valor por entrega personalizado (PJ)</Label>
                <Input type="number" step="0.01" value={form.pj_rate} onChange={(e) => setForm({ ...form, pj_rate: e.target.value })}
                  placeholder="Deixe em branco para usar o valor padrão PJ" data-testid="colab-pj-rate"
                  className="border-slate-700 bg-slate-950 text-slate-100" />
                <p className="text-xs text-slate-500">Sobrescreve o valor por entrega global apenas para este entregador.</p>
              </div>
            )}
            {edit && (
              <div className="space-y-2">
                <Label className="text-slate-300">Bases vinculadas</Label>
                <div className="flex flex-wrap gap-2" data-testid="colab-bases">
                  {baseList.length === 0 && <span className="text-xs text-slate-500">Nenhuma base cadastrada</span>}
                  {baseList.map((b) => {
                    const on = form.base_ids.includes(b.id);
                    return (
                      <button type="button" key={b.id} data-testid={`colab-base-${b.id}`}
                        onClick={() => setForm((f) => ({ ...f, base_ids: on ? f.base_ids.filter((x) => x !== b.id) : [...f.base_ids, b.id] }))}
                        className={`flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm transition-colors ${on ? "border-orange-500 bg-orange-500/15 text-orange-200" : "border-slate-700 bg-slate-950 text-slate-300 hover:border-slate-600"}`}>
                        {on && <Check className="h-3.5 w-3.5" />}{b.name}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-500">{form.base_ids.length} base(s) selecionada(s)</p>
              </div>
            )}
            {edit && isFuncionario(edit) && (
              <div className="space-y-2">
                <Label className="text-slate-300">Base de pagamento</Label>
                <Select value={form.pay_base || "none"} onValueChange={(v) => setForm((f) => ({ ...f, pay_base: v === "none" ? "" : v }))}>
                  <SelectTrigger className="border-slate-700 bg-slate-950 text-slate-100" data-testid="colab-pay-base"><SelectValue placeholder="Selecione a base que paga o salário" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none" data-testid="colab-pay-base-none">Não definida</SelectItem>
                    {(form.base_ids.length ? baseList.filter((b) => form.base_ids.includes(b.id)) : baseList).map((b) => (
                      <SelectItem key={b.id} value={b.id} data-testid={`colab-pay-base-${b.id}`}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!form.pay_base ? (
                  <p className="flex items-start gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-2 text-xs text-amber-200" data-testid="colab-pay-base-warning">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Defina a base de pagamento antes de gerar a folha. Sem ela, o colaborador aparece na folha e no custo de todas as bases vinculadas.
                  </p>
                ) : form.base_ids.length > 0 && !form.base_ids.includes(form.pay_base) ? (
                  <p className="text-xs text-amber-300" data-testid="colab-pay-base-unlinked">A base de pagamento precisa estar entre as bases vinculadas. Escolha outra ou vincule a base para salvar.</p>
                ) : (
                  <p className="text-xs text-slate-500">O salário entra só na folha e no Custo Operacional desta base. As outras bases vinculadas continuam só com acesso aos dados.</p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" className="text-slate-400" onClick={() => setEdit(null)}>Cancelar</Button>
            <Button className="bg-orange-500 text-white hover:bg-orange-600" onClick={saveEdit} disabled={busy === "edit" || (edit && isFuncionario(edit) && !!form.pay_base && form.base_ids.length > 0 && !form.base_ids.includes(form.pay_base))} data-testid="colab-save">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credentials */}
      <Dialog open={!!cred} onOpenChange={(o) => !o && setCred(null)}>
        <DialogContent className="max-w-md border-slate-800 bg-slate-900">
          <DialogHeader>
            <DialogTitle className="text-slate-100">Credenciais de acesso</DialogTitle>
            <DialogDescription className="text-slate-400">Redefina e-mail e/ou senha de {cred?.name}. Deixe a senha em branco para não alterá-la.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label className="text-slate-300">E-mail de login</Label><Input type="email" value={credForm.email} onChange={(e) => setCredForm({ ...credForm, email: e.target.value })} data-testid="colab-cred-email" className="border-slate-700 bg-slate-950 text-slate-100" /></div>
            <div className="space-y-2"><Label className="text-slate-300">Nova senha</Label><Input type="text" value={credForm.password} onChange={(e) => setCredForm({ ...credForm, password: e.target.value })} placeholder="(inalterada)" data-testid="colab-cred-password" className="border-slate-700 bg-slate-950 text-slate-100" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" className="text-slate-400" onClick={() => setCred(null)}>Cancelar</Button>
            <Button className="bg-orange-500 text-white hover:bg-orange-600" onClick={saveCred} disabled={busy === "cred"} data-testid="colab-cred-save">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Commute override */}
      <Dialog open={!!commute} onOpenChange={(o) => !o && setCommute(null)}>
        <DialogContent className="max-w-sm border-slate-800 bg-slate-900">
          <DialogHeader>
            <DialogTitle className="text-slate-100">Auxílio deslocamento</DialogTitle>
            <DialogDescription className="text-slate-400">Valor por dia personalizado para {commute?.name}. Deixe em branco para usar o padrão da categoria.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-slate-300">Valor por dia (R$)</Label>
            <Input type="number" step="0.01" value={commuteVal} onChange={(e) => setCommuteVal(e.target.value)} placeholder="Padrão" data-testid="colab-commute-input" className="border-slate-700 bg-slate-950 text-slate-100" />
          </div>
          <DialogFooter>
            <Button variant="ghost" className="text-slate-400" onClick={() => setCommute(null)}>Cancelar</Button>
            <Button className="bg-orange-500 text-white hover:bg-orange-600" onClick={saveCommute} disabled={busy === "commute"} data-testid="colab-commute-save">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permanent purge */}
      <Dialog open={!!purge} onOpenChange={(o) => !o && setPurge(null)}>
        <DialogContent className="max-w-md border-rose-500/30 bg-slate-900">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-300"><AlertOctagon className="h-5 w-5" /> Excluir permanentemente</DialogTitle>
            <DialogDescription className="text-slate-400">
              Isto remove <span className="font-semibold text-slate-200">{purge?.name}</span> e TODOS os seus dados da base (rotas, entregas, pontos, benefícios). Esta ação é irreversível. Para confirmar, digite o e-mail do colaborador abaixo.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300">E-mail de confirmação</Label>
              <Input value={purgeForm.email} onChange={(e) => setPurgeForm({ ...purgeForm, email: e.target.value })} placeholder={purge?.email || "e-mail do colaborador"} data-testid="colab-purge-email" className="border-slate-700 bg-slate-950 text-slate-100" />
              <p className="text-xs text-slate-500">Deve ser exatamente: {purge?.email}</p>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Sua senha de gestor (se solicitada)</Label>
              <Input type="password" value={purgeForm.password} onChange={(e) => setPurgeForm({ ...purgeForm, password: e.target.value })} placeholder="(opcional)" data-testid="colab-purge-password" className="border-slate-700 bg-slate-950 text-slate-100" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" className="text-slate-400" onClick={() => setPurge(null)}>Cancelar</Button>
            <Button className="bg-rose-600 text-white hover:bg-rose-700" onClick={confirmPurge}
              disabled={busy === "purge" || (purgeForm.email.trim().toLowerCase() !== (purge?.email || "").toLowerCase())}
              data-testid="colab-purge-confirm">Excluir definitivamente</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete */}
      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>        <AlertDialogContent className="border-slate-800 bg-slate-900">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-100">Remover colaborador?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">{del?.name} será removido permanentemente. Esta ação será registrada no log de exclusões.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-slate-700 bg-transparent text-slate-300">Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 text-white hover:bg-rose-700" onClick={confirmDelete} disabled={busy === "del"} data-testid="colab-delete-confirm">Remover</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {benefits && <BenefitsDialog collaborator={benefits} onClose={() => setBenefits(null)} />}
    </div>
  );
}
